import React, { Component, createRef } from "react";

// Cache: Map of query -> { results: [...], timestamp: number }
const fccSearchCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const API_TRIGGER_LENGTHS = new Set([3, 8, 15, 20, 30]);
const DEBOUNCE_MS = 300;
const AUTO_SELECT_MS = 300;

class FccFilerAutocomplete extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: props.value || "",
      results: [],
      showDropdown: false,
      loading: false,
      highlightedIndex: -1,
      noResults: false,
    };
    this.debounceTimer = null;
    this.autoSelectTimer = null;
    this.abortController = null;
    this.wrapperRef = createRef();
    this.inputRef = createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.autoSelectTimer) clearTimeout(this.autoSelectTimer);
    if (this.abortController) this.abortController.abort();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this.props.value !== this.state.query) {
      this.setState({ query: this.props.value || "" });
    }
  }

  handleClickOutside = (event) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ showDropdown: false });
    }
  };

  isNumericQuery = (q) => /^\d+$/.test(q.trim());

  shouldTriggerApi = (trimmedQuery) => {
    const len = trimmedQuery.length;
    // Trigger at specific lengths or when exactly 6 digits
    if (API_TRIGGER_LENGTHS.has(len)) return true;
    if (len === 6 && this.isNumericQuery(trimmedQuery)) return true;
    return false;
  };

  getCacheEntry = (query) => {
    const entry = fccSearchCache.get(query);
    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
      return entry.results;
    }
    if (entry) {
      fccSearchCache.delete(query);
    }
    return null;
  };

  findClosestCachedBase = (query) => {
    // Find the longest prefix in cache that is a substring of the current query
    let bestKey = null;
    let bestLen = 0;
    for (const [key] of fccSearchCache) {
      if (
        query.toLowerCase().startsWith(key.toLowerCase()) &&
        key.length > bestLen &&
        Date.now() - fccSearchCache.get(key).timestamp < CACHE_TTL
      ) {
        bestKey = key;
        bestLen = key.length;
      }
    }
    return bestKey;
  };

  filterResultsLocally = (results, query) => {
    const q = query.toLowerCase().trim();
    return results.filter(
      (item) =>
        (item.name && item.name.toLowerCase().includes(q)) ||
        (item.filer_id && item.filer_id.toLowerCase().includes(q))
    );
  };

  fetchResults = async (query) => {
    const { searchApi } = this.props;
    if (!searchApi) return;

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.setState({ loading: true });

    try {
      const response = await searchApi(query);
      const data = response?.data;

      if (data && (data.status === "success" || data.status === 200) && Array.isArray(data.data)) {
        const results = data.data;
        // Cache the results
        fccSearchCache.set(query.trim().toLowerCase(), {
          results,
          timestamp: Date.now(),
        });

        // Filter locally against the current query (might have changed)
        const currentQuery = this.state.query.trim();
        const filtered = currentQuery.length >= 3
          ? this.filterResultsLocally(results, currentQuery)
          : [];

        this.setState({
          results: filtered,
          loading: false,
          showDropdown: filtered.length > 0 || (currentQuery.length >= 3),
          noResults: filtered.length === 0 && currentQuery.length >= 3,
          highlightedIndex: -1,
        });

        // Auto-select: if exactly 6 numeric digits and only 1 result
        this.checkAutoSelect(filtered, currentQuery);
      } else {
        this.setState({
          results: [],
          loading: false,
          noResults: true,
          showDropdown: true,
          highlightedIndex: -1,
        });
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.setState({
          loading: false,
          results: [],
          noResults: true,
          showDropdown: true,
        });
      }
    }
  };

  checkAutoSelect = (results, query) => {
    if (this.autoSelectTimer) clearTimeout(this.autoSelectTimer);

    const trimmed = query.trim();
    if (
      trimmed.length === 6 &&
      this.isNumericQuery(trimmed) &&
      results.length === 1
    ) {
      this.autoSelectTimer = setTimeout(() => {
        if (this.state.query.trim() === trimmed && this.state.results.length === 1) {
          this.handleSelect(this.state.results[0]);
        }
      }, AUTO_SELECT_MS);
    }
  };

  handleInputChange = (e) => {
    const value = e.target.value;
    const trimmed = value.trim();

    this.setState({ query: value });

    // Notify parent of raw value change
    if (this.props.onInputChange) {
      this.props.onInputChange(value);
    }

    // Clear auto-select timer
    if (this.autoSelectTimer) clearTimeout(this.autoSelectTimer);

    // If field is empty, clear cache and close dropdown
    if (trimmed === "") {
      fccSearchCache.clear();
      this.setState({
        results: [],
        showDropdown: false,
        loading: false,
        noResults: false,
        highlightedIndex: -1,
      });
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      return;
    }

    // If less than 3 chars, close dropdown
    if (trimmed.length < 3) {
      this.setState({
        results: [],
        showDropdown: false,
        noResults: false,
        highlightedIndex: -1,
      });
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      return;
    }

    // Check exact cache hit
    const cached = this.getCacheEntry(trimmed.toLowerCase());
    if (cached) {
      const filtered = this.filterResultsLocally(cached, trimmed);
      this.setState({
        results: filtered,
        showDropdown: true,
        noResults: filtered.length === 0,
        loading: false,
        highlightedIndex: -1,
      });
      this.checkAutoSelect(filtered, trimmed);

      // If this length should trigger API and NOT already cached at this exact query, still trigger
      if (this.shouldTriggerApi(trimmed) && !this.getCacheEntry(trimmed.toLowerCase())) {
        // Already cached, skip
      }
      return;
    }

    // Check closest cached base for local filtering
    const closestKey = this.findClosestCachedBase(trimmed.toLowerCase());
    if (closestKey) {
      const baseResults = fccSearchCache.get(closestKey).results;
      const filtered = this.filterResultsLocally(baseResults, trimmed);
      this.setState({
        results: filtered,
        showDropdown: true,
        noResults: filtered.length === 0,
        loading: false,
        highlightedIndex: -1,
      });
      this.checkAutoSelect(filtered, trimmed);
    }

    // Determine if we should trigger an API call
    if (this.shouldTriggerApi(trimmed)) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchResults(trimmed);
      }, DEBOUNCE_MS);
    } else if (!closestKey) {
      // No cache at all and not an API trigger length
      // Show what we have (empty) - the user will get results at the next trigger length
      this.setState({
        showDropdown: trimmed.length >= 3,
        noResults: true,
      });
    }
  };

  handleSelect = (item) => {
    if (this.autoSelectTimer) clearTimeout(this.autoSelectTimer);

    this.setState({
      query: item.filer_id,
      showDropdown: false,
      results: [],
      noResults: false,
      highlightedIndex: -1,
    });

    if (this.props.onSelect) {
      this.props.onSelect(item);
    }
  };

  handleKeyDown = (e) => {
    const { results, highlightedIndex, showDropdown } = this.state;

    if (!showDropdown || results.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        if (this.props.onEnterKey) {
          this.props.onEnterKey();
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.setState((prev) => ({
          highlightedIndex: Math.min(prev.highlightedIndex + 1, prev.results.length - 1),
        }));
        break;
      case "ArrowUp":
        e.preventDefault();
        this.setState((prev) => ({
          highlightedIndex: Math.max(prev.highlightedIndex - 1, 0),
        }));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          this.handleSelect(results[highlightedIndex]);
        } else if (this.props.onEnterKey) {
          this.props.onEnterKey();
        }
        break;
      case "Escape":
        this.setState({ showDropdown: false, highlightedIndex: -1 });
        break;
      default:
        break;
    }
  };

  handleBlur = () => {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      const trimmed = this.state.query.trim();

      // If query >= 3 chars and no cached results, force API call
      if (trimmed.length >= 3 && !this.getCacheEntry(trimmed.toLowerCase())) {
        const closestKey = this.findClosestCachedBase(trimmed.toLowerCase());
        if (!closestKey) {
          this.fetchResults(trimmed);
        }
      }

      // Close dropdown
      this.setState({ showDropdown: false });

      // Call parent blur handler
      if (this.props.onBlur) {
        this.props.onBlur();
      }
    }, 200);
  };

  handleFocus = () => {
    const trimmed = this.state.query.trim();
    if (trimmed.length >= 3) {
      // Show any cached results
      const cached = this.getCacheEntry(trimmed.toLowerCase());
      if (cached) {
        const filtered = this.filterResultsLocally(cached, trimmed);
        if (filtered.length > 0) {
          this.setState({ showDropdown: true, results: filtered, noResults: false });
          return;
        }
      }
      const closestKey = this.findClosestCachedBase(trimmed.toLowerCase());
      if (closestKey) {
        const baseResults = fccSearchCache.get(closestKey).results;
        const filtered = this.filterResultsLocally(baseResults, trimmed);
        if (filtered.length > 0) {
          this.setState({ showDropdown: true, results: filtered, noResults: false });
          return;
        }
      }
    }
  };

  formatDisplayText = (item, query) => {
    const isNumeric = this.isNumericQuery(query.trim());
    if (isNumeric) {
      return (
        <span>
          <strong>{item.filer_id}</strong> - {item.name}
        </span>
      );
    }
    return (
      <span>
        <strong>{item.name}</strong> ({item.filer_id})
      </span>
    );
  };

  render() {
    const {
      query,
      results,
      showDropdown,
      loading,
      highlightedIndex,
      noResults,
    } = this.state;

    const {
      id,
      name,
      placeholder,
      disabled,
      errorClass,
      required,
      maxLength,
      className,
      style,
    } = this.props;

    return (
      <div ref={this.wrapperRef} style={{ position: "relative" }}>
        <input
          ref={this.inputRef}
          type="text"
          className={className || "form-control"}
          id={id || "filer_id_fcc"}
          name={name || "filer_id"}
          value={query}
          onChange={this.handleInputChange}
          onKeyDown={this.handleKeyDown}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          placeholder={placeholder || "Search by Filer ID or Company Name"}
          disabled={disabled}
          autoComplete="off"
          style={style}
          maxLength={maxLength}
        />

        {/* Loading spinner inside input */}
        {loading && (
          <div
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                width: "18px",
                height: "18px",
                border: "2px solid #e0e0e0",
                borderTopColor: "#6576ff",
                borderRadius: "50%",
                animation: "fcc-spin 0.6s linear infinite",
              }}
            />
          </div>
        )}

        {/* Dropdown */}
        {showDropdown && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1050,
              backgroundColor: "#fff",
              border: "1px solid #dbdfea",
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
              maxHeight: "300px",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            {loading && results.length === 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  color: "#8094ae",
                  fontSize: "13px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #e0e0e0",
                    borderTopColor: "#6576ff",
                    borderRadius: "50%",
                    animation: "fcc-spin 0.6s linear infinite",
                    display: "inline-block",
                    marginRight: "8px",
                    verticalAlign: "middle",
                  }}
                />
                Searching...
              </div>
            )}

            {!loading && noResults && (
              <div
                style={{
                  padding: "12px 16px",
                  color: "#8094ae",
                  fontSize: "13px",
                  textAlign: "center",
                }}
              >
                No results found
              </div>
            )}

            {results.map((item, index) => (
              <div
                key={item.filer_id + "-" + index}
                onMouseDown={(e) => {
                  e.preventDefault();
                  this.handleSelect(item);
                }}
                onMouseEnter={() => this.setState({ highlightedIndex: index })}
                style={{
                  padding: "10px 16px",
                  cursor: "pointer",
                  backgroundColor: highlightedIndex === index ? "#f0f3ff" : "#fff",
                  borderBottom: index < results.length - 1 ? "1px solid #f5f6fa" : "none",
                  fontSize: "13px",
                  color: "#364a63",
                  transition: "background-color 0.15s ease",
                }}
              >
                {this.formatDisplayText(item, query)}
              </div>
            ))}
          </div>
        )}

        {/* CSS Animation for spinner */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
              @keyframes fcc-spin {
                to { transform: rotate(360deg); }
              }
            `,
          }}
        />
      </div>
    );
  }
}

export default FccFilerAutocomplete;
